import { User } from 'firebase/auth';
import { UserProfile, ExternalTask } from '../types';
import { IntegrationService } from '../services/IntegrationService';

interface IntegrationEngineProps {
    user: User | null;
    userProfile: UserProfile | null;
    setUserProfile: (profile: UserProfile | null) => void;
    showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
    track: (eventName: string, properties?: any) => void;
}

export const useIntegrationEngine = ({ user, userProfile, setUserProfile, showToast, track }: IntegrationEngineProps) => {
    
    const createExternalTask = async (data: {
        projectKey: string,
        title: string,
        assigneeId: string,
        priority: string
    }) => {
        try {
            track('external_task_creation_triggered', { projectKey: data.projectKey, priority: data.priority });
            await IntegrationService.createExternalTask(data);
            showToast("Created external task!");
        } catch (e: any) {
            showToast("Failed to create task", "error");
            throw e;
        }
    };

    const syncIntegrations = async () => {
        try {
            track('manual_integration_sync_triggered');
            await IntegrationService.syncExternalTasks();
            showToast("Sync started...");
        } catch (e: any) {
            showToast("Sync failed", "error");
        }
    };

    const toggleProviderSync = async (provider: string, projectKey: string) => {
        if (!user || !userProfile) return;
        console.log(`[Integration] Toggling sync preference for ${projectKey}`);

        const currentIntegration = userProfile.integrations?.[provider] || { enabled: true, disabledProjectKeys: [] };
        const newDisabledKeys = [...(currentIntegration.disabledProjectKeys || [])];
        const isCurrentlyDisabled = newDisabledKeys.includes(projectKey);
        track('integration_project_toggled', { provider, projectKey, enabled: isCurrentlyDisabled });
        const updatedKeys = isCurrentlyDisabled 
            ? newDisabledKeys.filter(k => k !== projectKey)
            : [...newDisabledKeys, projectKey];

        const newIntegrations = {
            ...userProfile.integrations,
            [provider]: {
                enabled: true,
                disabledProjectKeys: updatedKeys
            }
        };

        // 1. Optimistic UI update
        setUserProfile({
            ...userProfile,
            integrations: newIntegrations
        });

        // 2. Persist to DB
        try {
            await IntegrationService.updateSyncPreference(user.uid, provider, updatedKeys);
            console.log(`[Integration] Sync preference updated successfully for ${projectKey}`);
            showToast(isCurrentlyDisabled ? "Project sync enabled" : "Project sync disabled");
        } catch (e) {
            console.error("[Integration Error] Failed to toggle sync preference:", e);
            showToast("Failed to update preferences", "error");
            // Revert optimistic update
            setUserProfile(userProfile);
        }
    };

    const updateExternalTaskStatus = async (extTask: ExternalTask, newStatus: string, blockedReason?: string, newAssigneeId?: string, newTitle?: string, newDescription?: string, newPriority?: string) => {
        const payload: any = {
            taskId: extTask.id,
            provider: extTask.externalSource,
            externalId: extTask.externalId,
            newStatus: newStatus,
            blockedReason
        };

        if (newTitle !== undefined) payload.newTitle = newTitle;
        if (newDescription !== undefined) payload.newDescription = newDescription;
        if (newPriority !== undefined) payload.newPriority = newPriority;

        // Only include newAssigneeId if it was explicitly passed (even if "")
        if (newAssigneeId !== undefined) {
            payload.newAssigneeId = newAssigneeId;
        }

        try {
            await IntegrationService.updateExternalTaskStatus(payload, extTask.externalSource);
            showToast("Updated external task!");
        } catch (e: any) {
            showToast("Failed to update external task", "error");
            throw e;
        }
    };

    return {
        createExternalTask,
        syncIntegrations,
        toggleProviderSync,
        updateExternalTaskStatus
    };
};
